import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnChanges, inject } from '@angular/core'
import { ConfirmationService, MessageService } from 'primeng/api'

import { ApiPreofferService } from '@data/services/modules/preoffer/api-preoffer.service'
import { AuthService } from '@data/services/modules/auth/auth.service'
import { ClientPreoffers } from '@data/interfaces/preoffer.interface'
import { guardarBlobComo } from '@data/utils/descarga-archivo.util'
import { PreofferView, preofferView } from '../preoffer-view.util'

/**
 * Plan v4.3 §9.3 y §2b · Tarjeta «Preoferta» arriba de la Calculadora del cliente, la misma con la que decide (D-74): el
 * estado con su tono, el PDF y «Reenviar» de una emitida, y las evaluaciones anteriores en un `<details>`. Lo decidido
 * en sombra solo llega a quien tiene `view-shadow` (lo filtra la API).
 */
@Component({
  selector: 'app-preoffer-card',
  templateUrl: './preoffer-card.component.html',
  styleUrls: ['./preoffer-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ConfirmationService, MessageService],
})
export class PreofferCardComponent implements OnChanges {
  @Input() clientId: string | null = null

  private readonly api = inject(ApiPreofferService)
  private readonly cdr = inject(ChangeDetectorRef)
  private readonly confirmation = inject(ConfirmationService)
  private readonly messages = inject(MessageService)
  private readonly auth = inject(AuthService)

  readonly canView = this.auth.hasModulePermission('lease-preoffer', 'view')
  readonly canDownload = this.auth.hasModulePermission('lease-preoffer', 'download')
  readonly canResend = this.auth.hasModulePermission('lease-preoffer', 'resend')

  state: 'loading' | 'ready' | 'error' = 'loading'
  errorMsg = ''
  data: ClientPreoffers | null = null
  view: PreofferView | null = null
  busy = false

  ngOnChanges(): void {
    this.load()
  }

  load(refresh = false): void {
    if (!this.clientId || !this.canView) return
    if (refresh) this.api.refresh()
    this.state = 'loading'
    this.api.byClient(this.clientId).subscribe((response) => {
      this.state = response.error ? 'error' : 'ready'
      this.errorMsg = response.msg
      this.data = response.data
      this.view = preofferView(response.data?.current)
      this.cdr.markForCheck()
    })
  }

  historyView(item: ClientPreoffers['history'][number]): PreofferView | null {
    return preofferView(item)
  }

  download(): void {
    const id = this.data?.current?._id
    if (!id || this.busy) return
    this.busy = true
    this.api.document(id).subscribe((response) => {
      this.busy = false
      if (response.error || !response.data) this.messages.add({ key: 'preoffer', severity: 'error', summary: 'No se pudo abrir el PDF', detail: response.msg, life: 5000 })
      else guardarBlobComo(response.data.blob, response.data.fileName)
      this.cdr.markForCheck()
    })
  }

  resend(): void {
    const current = this.data?.current
    if (!current || this.busy) return
    this.confirmation.confirm({
      key: 'preoffer',
      header: 'Reenviar la preoferta',
      message: '¿Reenviar la preoferta al correo del cliente? Es el mismo PDF; la vigencia no cambia.',
      acceptLabel: 'Reenviar',
      rejectLabel: 'Cancelar',
      accept: () => {
        this.busy = true
        this.cdr.markForCheck()
        this.api.resend(current._id).subscribe((response) => {
          this.busy = false
          if (response.error) this.messages.add({ key: 'preoffer', severity: 'error', summary: 'No se pudo reenviar', detail: response.msg, life: 5000 })
          else this.messages.add({ key: 'preoffer', severity: 'success', summary: 'Preoferta en cola de envío', detail: 'Sale en el siguiente minuto.', life: 4000 })
          this.load(true)
        })
      },
    })
  }
}
